import { Component, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ComposeDraft, UpdoxDirectMessage } from '../updox-direct-message';
import { UpdoxMessagesService, PatientDocument } from '../updox-messages.service';
import { ContactSearchResult } from '../updox-contact';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

@Component({
  selector: 'app-updox-compose',
  templateUrl: './compose.component.html',
})
export class UpdoxComposeComponent implements OnDestroy {
  @Input() draft: ComposeDraft;
  @Input() practiceId: number;
  @Output() close = new EventEmitter<void>();
  @Output() sent = new EventEmitter<UpdoxDirectMessage>();
  @Output() savedAsDraft = new EventEmitter<UpdoxDirectMessage>();

  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

  isSending = false;
  isSavingDraft = false;

  // Outbound attachments — from computer
  attachedFiles: File[] = [];

  // Outbound attachments — from patient chart
  chartAttachedDocs: PatientDocument[] = [];
  showChartPanel = false;
  chartPatient: any = null;
  chartPatientQuery = '';
  chartPatientResults: any[] = [];
  chartPatientSearchLoading = false;
  showChartPatientDropdown = false;
  private chartPatientSearchTimer: any;
  private chartPatientSearchVersion = 0;
  chartDocuments: PatientDocument[] = [];
  chartDocumentsLoading = false;
  chartDocFilter = '';
  selectedChartDocIds: number[] = [];

  // Typeahead state
  toSearchResults: ContactSearchResult[] = [];
  showToDropdown = false;
  toSearchLoading = false;
  private toSearchTimer: any;
  private searchVersion = 0; // incremented on each new search; used to discard stale responses

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnDestroy(): void {
    clearTimeout(this.toSearchTimer);
    clearTimeout(this.chartPatientSearchTimer);
  }

  toggleMinimize(): void {
    this.draft.minimized = !this.draft.minimized;
    if (this.draft.minimized) {
      // Discard any pending search and close dropdown when minimizing.
      clearTimeout(this.toSearchTimer);
      this.toSearchLoading = false;
      this.showToDropdown = false;
      this.toSearchResults = [];
    }
  }

  // --- Typeahead ---

  onToInput(value: string): void {
    // Clear stale recipient info whenever the user edits the address field.

    clearTimeout(this.toSearchTimer);
    this.toSearchResults = [];
    this.showToDropdown = false;

    if (!value || value.trim().length < 2) {
      this.toSearchLoading = false;
      return;
    }

    this.toSearchLoading = true;
    this.toSearchTimer = setTimeout(() => this.runSearch(value.trim()), 300);
  }

  private runSearch(query: string): void {
    if (!this.practiceId) { this.toSearchLoading = false; return; }

    // Stamp this search so a slower earlier response can't overwrite a newer one.
    const version = ++this.searchVersion;

    this.messagesService.searchContacts(this.practiceId, query)
      .then(resp => {
        if (version !== this.searchVersion) { return; } // stale — a newer search is in flight
        this.toSearchLoading = false;

        const local: ContactSearchResult[] = (resp.wellwink_contacts || []).map((c: any) => ({
          id: c.id,
          name: c.name || '',
          direct_address: c.direct_address || '',
          organization: c.organization || '',
          specialty: c.specialty || '',
          source: 'local' as const,
        }));

        const directory: ContactSearchResult[] = this.normalizeDirectoryResults(resp.directory_results);

        this.toSearchResults = [...local, ...directory];
        this.showToDropdown = this.toSearchResults.length > 0;
      })
      .catch(() => {
        if (version !== this.searchVersion) { return; }
        this.toSearchLoading = false;
        // Silent — typeahead failure is non-critical; user can still type an address manually.
      });
  }

  // Updox returns { totalCount: N, searchResults: [...] }.
  // Each hit uses firstName/lastName and businessName.
  // Also handles a plain array in case the shape ever changes.
  private normalizeDirectoryResults(raw: any): ContactSearchResult[] {
    if (!raw) { return []; }
    let items: any[];
    if (Array.isArray(raw)) {
      items = raw;
    } else if (Array.isArray(raw.searchResults)) {
      items = raw.searchResults;
    } else {
      // Fallback: find the first array value in the object.
      items = (Object.values(raw).find(v => Array.isArray(v)) as any[]) || [];
    }
    return items
      .map(hit => ({
        name: (hit.firstName || hit.lastName)
          ? `${hit.firstName || ''} ${hit.lastName || ''}`.trim()
          : hit.displayName || hit.name || '',
        direct_address: hit.directAddress || hit.direct_address || '',
        organization: hit.businessName || hit.organization || '',
        specialty: hit.specialty || '',
        source: 'directory' as const,
      }))
      .filter(r => !!r.direct_address);
  }

  selectToContact(result: ContactSearchResult): void {
    this.draft.to_address = result.direct_address;
    this.toSearchResults = [];
    this.showToDropdown = false;
  }

  closeDropdownDelayed(): void {
    // Delay so mousedown on a dropdown option fires before the dropdown disappears.
    setTimeout(() => { this.showToDropdown = false; }, 150);
  }

  get localResults(): ContactSearchResult[] {
    return this.toSearchResults.filter(r => r.source === 'local');
  }

  get directoryResults(): ContactSearchResult[] {
    return this.toSearchResults.filter(r => r.source === 'directory');
  }

  // --- Attachments ---

  triggerFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) { return; }
    Array.from(input.files).forEach(f => {
      const alreadyAdded = this.attachedFiles.some(e => e.name === f.name && e.size === f.size);
      if (!alreadyAdded) { this.attachedFiles.push(f); }
    });
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.attachedFiles.splice(index, 1);
  }

  fileIcon(file: File): string {
    if (file.type.includes('pdf'))                                        { return 'fa-file-pdf-o text-danger'; }
    if (file.type.includes('xml') || file.name.toLowerCase().endsWith('.xml')) { return 'fa-file-code-o text-info'; }
    if (file.type.includes('image'))                                      { return 'fa-file-image-o text-warning'; }
    return 'fa-file-o text-muted';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024)           { return bytes + ' B'; }
    if (bytes < 1024 * 1024)    { return (bytes / 1024).toFixed(1) + ' KB'; }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // --- Chart panel ---

  toggleChartPanel(): void {
    this.showChartPanel = !this.showChartPanel;
    if (this.showChartPanel && !this.chartPatient && this.draft.patient_id) {
      this.autoLoadChartPatient();
    }
  }

  private autoLoadChartPatient(): void {
    this.chartPatientSearchLoading = true;
    this.messagesService.getPatientById(this.draft.patient_id, this.practiceId)
      .then(p => {
        this.chartPatientSearchLoading = false;
        if (p) { this.selectChartPatient(p); }
      })
      .catch(() => { this.chartPatientSearchLoading = false; });
  }

  onChartPatientQueryChange(value: string): void {
    this.chartPatient = null;
    this.chartDocuments = [];
    this.selectedChartDocIds = [];
    clearTimeout(this.chartPatientSearchTimer);
    this.chartPatientResults = [];
    this.showChartPatientDropdown = false;
    if (!value || value.trim().length < 2) {
      this.chartPatientSearchLoading = false;
      return;
    }
    this.chartPatientSearchLoading = true;
    this.chartPatientSearchTimer = setTimeout(() => this.runChartPatientSearch(value.trim()), 300);
  }

  private runChartPatientSearch(query: string): void {
    const version = ++this.chartPatientSearchVersion;
    this.messagesService.searchPatients(query, this.practiceId)
      .then(results => {
        if (version !== this.chartPatientSearchVersion) { return; }
        this.chartPatientSearchLoading = false;
        this.chartPatientResults = results || [];
        this.showChartPatientDropdown = this.chartPatientResults.length > 0;
      })
      .catch(() => {
        if (version !== this.chartPatientSearchVersion) { return; }
        this.chartPatientSearchLoading = false;
      });
  }

  selectChartPatient(p: any): void {
    this.chartPatient = p;
    this.chartPatientQuery = p.name || '';
    this.chartPatientResults = [];
    this.showChartPatientDropdown = false;
    this.selectedChartDocIds = [];
    this.loadChartDocuments(p.id);
  }

  private loadChartDocuments(patientId: number): void {
    this.chartDocumentsLoading = true;
    this.chartDocuments = [];
    this.messagesService.getPatientDocuments(patientId)
      .then(docs => {
        this.chartDocumentsLoading = false;
        this.chartDocuments = docs;
      })
      .catch(() => { this.chartDocumentsLoading = false; });
  }

  changeChartPatient(): void {
    this.chartPatient = null;
    this.chartPatientQuery = '';
    this.chartDocuments = [];
    this.selectedChartDocIds = [];
  }

  toggleChartDoc(id: number): void {
    if (this.selectedChartDocIds.includes(id)) {
      this.selectedChartDocIds = this.selectedChartDocIds.filter(x => x !== id);
    } else {
      this.selectedChartDocIds = [...this.selectedChartDocIds, id];
    }
  }

  isDocSelected(id: number): boolean {
    return this.selectedChartDocIds.includes(id);
  }

  attachFromChart(): void {
    const toAdd = this.chartDocuments.filter(d => this.selectedChartDocIds.includes(d.id));
    toAdd.forEach(doc => {
      if (!this.chartAttachedDocs.some(d => d.id === doc.id)) {
        this.chartAttachedDocs.push(doc);
      }
    });
    this.showChartPanel = false;
    this.selectedChartDocIds = [];
  }

  removeChartDoc(id: number): void {
    this.chartAttachedDocs = this.chartAttachedDocs.filter(d => d.id !== id);
  }

  closeChartPatientDropdownDelayed(): void {
    setTimeout(() => { this.showChartPatientDropdown = false; }, 150);
  }

  get filteredChartDocs(): PatientDocument[] {
    if (!this.chartDocFilter.trim()) { return this.chartDocuments; }
    const term = this.chartDocFilter.toLowerCase();
    return this.chartDocuments.filter(d =>
      d.name?.toLowerCase().includes(term) ||
      d.document_type_name?.toLowerCase().includes(term) ||
      d.file_name?.toLowerCase().includes(term)
    );
  }

  get groupedChartDocs(): { typeName: string; docs: PatientDocument[] }[] {
    const map = new Map<string, PatientDocument[]>();
    this.filteredChartDocs.forEach(doc => {
      const key = doc.document_type_name || 'Other';
      if (!map.has(key)) { map.set(key, []); }
      map.get(key).push(doc);
    });
    return Array.from(map.entries()).map(([typeName, docs]) => ({ typeName, docs }));
  }

  get totalAttachedCount(): number {
    return this.attachedFiles.length + this.chartAttachedDocs.length;
  }

  get selectedChartDocCount(): number {
    return this.selectedChartDocIds.length;
  }

  trackByTypeName(_: number, group: { typeName: string }): string { return group.typeName; }
  trackByDocId(_: number, doc: PatientDocument): number { return doc.id; }

  chartDocIcon(contentType: string): string {
    if (!contentType) { return 'fa-file-o text-muted'; }
    if (contentType.includes('pdf'))                                    { return 'fa-file-pdf-o text-danger'; }
    if (contentType.includes('xml') || contentType.includes('text/xml')){ return 'fa-file-code-o text-info'; }
    if (contentType.includes('image'))                                  { return 'fa-file-image-o text-warning'; }
    if (contentType.includes('word') || contentType.includes('doc'))    { return 'fa-file-word-o text-primary'; }
    return 'fa-file-o text-muted';
  }

  patientInitialFor(p: any): string {
    return p?.name?.charAt(0)?.toUpperCase() || '?';
  }

  patientDemographics(p: any): string {
    if (!p?.complete_detail) { return ''; }
    const dashIdx = p.complete_detail.lastIndexOf(' - ');
    const head = dashIdx >= 0 ? p.complete_detail.substring(0, dashIdx) : p.complete_detail;
    const commaIdx = head.indexOf(', ');
    return commaIdx >= 0 ? head.substring(commaIdx + 2) : '';
  }

  // --- Send / Draft ---

  send(): void {
    if (!this.draft.to_address.trim()) { return; }
    this.isSending = true;

    const message = new UpdoxDirectMessage();
    message.practice_id          = this.practiceId;
    message.to_address           = this.draft.to_address;
    message.subject              = this.draft.subject;
    message.body_text            = this.draft.body_text;
    message.patient_id           = this.draft.patient_id || null;
    message.reply_to_message_id  = this.draft.reply_to_message_id || null;

    const chartDocumentIds = this.chartAttachedDocs.map(d => d.id);
    this.messagesService.sendMessage(message, this.attachedFiles, chartDocumentIds)
      .then(resp => {
        this.isSending = false;
        const updoxResp = resp?.updox_response;
        if (updoxResp && !updoxResp.successful) {
          this.globalErrorHandler.error = updoxResp.responseMessage || 'Failed to send message.';
          return;
        }
        // If this compose was opened from a saved draft, remove the local draft record.
        if (this.draft.draftId) {
          this.messagesService.deleteMessage(this.draft.draftId).catch(() => {});
        }
        this.attachedFiles = [];
        this.chartAttachedDocs = [];
        const sentMessage = new UpdoxDirectMessage().load_from_json(resp?.message || {});
        this.alertService.success('Message sent successfully.', this.alertService.options);
        this.sent.emit(sentMessage);
        this.close.emit();
      })
      .catch(resp => {
        this.isSending = false;
        const body = resp?.error;
        this.globalErrorHandler.error = body?.responseMessage || body?.error || 'Failed to send message. Please try again.';
      });
  }

  saveDraft(): void {
    if (!this.draft.to_address.trim() && !this.draft.subject.trim() && !this.draft.body_text.trim()) { return; }
    this.isSavingDraft = true;

    const message = new UpdoxDirectMessage();
    message.practice_id            = this.practiceId;
    message.to_address             = this.draft.to_address;
    message.subject                = this.draft.subject;
    message.body_text              = this.draft.body_text;
    message.patient_id             = this.draft.patient_id || null;
    message.reply_to_message_id    = this.draft.reply_to_message_id || null;

    const isUpdate = !!this.draft.draftId;
    const request = isUpdate
      ? this.messagesService.updateDraft(this.draft.draftId, message)
      : this.messagesService.saveDraft(message);

    request
      .then(resp => {
        this.isSavingDraft = false;
        const savedMessage = new UpdoxDirectMessage().load_from_json(resp || {});
        if (!isUpdate) {
          this.draft.draftId = savedMessage.id;
        }
        this.alertService.success('Draft saved.', this.alertService.options);
        this.savedAsDraft.emit(savedMessage);
        this.close.emit();
      })
      .catch(resp => {
        this.isSavingDraft = false;
        const body = resp?.error;
        this.globalErrorHandler.error = body?.error || 'Failed to save draft. Please try again.';
      });
  }
}
